// src/utils/parseStringDate.js

/**
 * Parse various date string formats into a Date object
 *
 * This utility function handles multiple date formats commonly used in the application:
 * - DD/MM/YYYY format (British format)
 * - ISO date strings (YYYY-MM-DD)
 * - DateTime strings with various formats
 * - Localized date strings
 *
 * @param {string|Date} dateString - The date string to parse
 * @returns {Date} - A valid Date object
 */
const parseStringDate = (dateString) => {
  // If already a Date object, return as is
  if (dateString instanceof Date) {
    return dateString;
  }

  // If null, undefined, or empty string, return current date
  if (!dateString || dateString === "") {
    return new Date();
  }

  // Convert to string if not already
  const dateStr = String(dateString).trim();

  // Handle DD/MM/YYYY format (British format)
  if (dateStr.match(/^\d{1,2}\/\d{1,2}\/\d{4}$/)) {
    const [day, month, year] = dateStr.split("/");
    // Month is 0-indexed in JavaScript Date constructor
    return new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
  }

  // Handle DD/MM/YYYY HH:mm format (British format with time)
  if (dateStr.match(/^\d{1,2}\/\d{1,2}\/\d{4}\s+\d{1,2}:\d{2}$/)) {
    const [datePart, timePart] = dateStr.split(" ");
    const [day, month, year] = datePart.split("/");
    const [hours, minutes] = timePart.split(":");

    return new Date(
      parseInt(year),
      parseInt(month) - 1,
      parseInt(day),
      parseInt(hours),
      parseInt(minutes)
    );
  }

  // Handle DD/MM/YYYY, HH:mm (with comma)
  if (dateStr.match(/^\d{1,2}\/\d{1,2}\/\d{4},\s*\d{1,2}:\d{2}$/)) {
    const [datePart, timePart] = dateStr.split(",").map((s) => s.trim());
    const [day, month, year] = datePart.split("/");
    const [hours, minutes] = timePart.split(":");

    return new Date(
      parseInt(year),
      parseInt(month) - 1,
      parseInt(day),
      parseInt(hours),
      parseInt(minutes)
    );
  }

  // Handle DD/MM/YYYY, HH:mm AM/PM format
  if (dateStr.match(/^\d{1,2}\/\d{1,2}\/\d{4},?\s*\d{1,2}:\d{2}\s*(AM|PM)$/i)) {
    const parts = dateStr.replace(",", "").split(" ");
    const datePart = parts[0];
    const timePart = parts[1];
    const meridiem = parts[2]?.toUpperCase();

    const [day, month, year] = datePart.split("/");
    const [hours, minutes] = timePart.split(":");

    let hour24 = parseInt(hours);
    if (meridiem === "PM" && hour24 !== 12) {
      hour24 += 12;
    } else if (meridiem === "AM" && hour24 === 12) {
      hour24 = 0;
    }

    return new Date(
      parseInt(year),
      parseInt(month) - 1,
      parseInt(day),
      hour24,
      parseInt(minutes)
    );
  }

  // Handle YYYY-MM-DD format (ISO date)
  if (dateStr.match(/^\d{4}-\d{1,2}-\d{1,2}$/)) {
    return new Date(dateStr);
  }

  // Handle YYYY-MM-DDTHH:mm format (ISO datetime)
  if (dateStr.match(/^\d{4}-\d{1,2}-\d{1,2}T\d{1,2}:\d{2}/)) {
    return new Date(dateStr);
  }

  // Handle locale date strings (try native parsing)
  try {
    const parsed = new Date(dateStr);
    // Check if the parsed date is valid
    if (!isNaN(parsed.getTime())) {
      return parsed;
    }
  } catch (error) {
    console.warn("Failed to parse date string:", dateStr, error);
  }

  // If all parsing attempts fail, try to handle common variations
  try {
    // Replace common separators and try again
    const normalizedDate = dateStr
      .replace(/[-_.]/g, "/") // Replace hyphens, dots, underscores with slashes
      .replace(/\s+/g, " ") // Normalize whitespace
      .trim();

    // Try DD/MM/YYYY format again after normalization
    if (normalizedDate.match(/^\d{1,2}\/\d{1,2}\/\d{4}/)) {
      const [day, month, year] = normalizedDate.split("/");
      return new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
    }

    // Last resort: try native Date parsing on normalized string
    const lastAttempt = new Date(normalizedDate);
    if (!isNaN(lastAttempt.getTime())) {
      return lastAttempt;
    }
  } catch (error) {
    console.warn("Date normalization failed:", dateStr, error);
  }

  // If everything fails, return current date and log warning
  console.warn("Could not parse date string, returning current date:", dateStr);
  return new Date();
};

export default parseStringDate;
